import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { WalletService } from '../../../services/wallet.service';
import { Web3ServiceService } from '../../../services/web3-service.service';
import { Web2ServiceService } from '../../../services/web2-service.service';
import { ToastService } from '../../../services/toast.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { parseUnits } from 'viem';
import { OrderSide } from '../../../common/constants/common.constants';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-sellno-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sellno-modal.component.html',
  styleUrl: './sellno-modal.component.css'
})
export class SellnoModalComponent implements OnInit, OnDestroy {
  @Input() position: any = null;
  @Output() close = new EventEmitter<void>();
  @Output() editOrder = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  isCashEnabled: boolean = false;
  displayShares: string = '';
  displayValue: string = '';
  displayNow: string = '';
  isProcessing: boolean = false;
  private latestStreamData: any = null;
  private cashoutStreamSubscription?: Subscription;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private walletService: WalletService,
    private web3Service: Web3ServiceService,
    private web2Service: Web2ServiceService,
    private toastr: ToastService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.displayShares = '...';
    this.displayValue = '...';
    this.displayNow = '...';
    this.startCashoutStream();
  }

  startCashoutStream() {
    const userData = this.auth.getUserData();
    const userId = userData?.id;
    const outcomeId = this.position?.outcome_id;

    if (!userId || !outcomeId) return;

    this.cashoutStreamSubscription = this.api.streamOutcomePosition(outcomeId, userId).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        if (data) {
          this.latestStreamData = data;

          if (data.cash_enabled !== undefined) {
            this.isCashEnabled = data.cash_enabled;
          }

          let shares = Number(data.shares || 0);
          this.displayShares = `${shares} Shares`;

          // Individual share price in cents and total value in USD
          if (data.avg_price !== undefined) {
            const rawPrice = parseFloat(data.avg_price);
            let priceInDollars = 0;

            if (rawPrice > 0 && rawPrice <= 1) {
              priceInDollars = rawPrice;
            } else {
              priceInDollars = rawPrice / 100;
            }

            this.displayNow = `${Math.round(priceInDollars * 100)}¢`;
            
            const totalValue = shares * priceInDollars;
            this.displayValue = `$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }

        if (res?.status === 'success' || res?.message === 'success' || res?.data?.success === true) {
          this.toastr.success('Cashout successful');
          this.success.emit();
        }
      },
      error: (err) => console.error("Cashout Stream Error:", err)
    });
  }

  async onCashout() {
    if (!this.isCashEnabled || this.isProcessing) return;
    if (!this.latestStreamData) {
      this.toastr.error('No streaming data available yet');
      return;
    }

    this.isProcessing = true;
    this.cdRef.detectChanges();

    try {
      const userData = this.auth.getUserData();
      const userAddress = userData?.proxy_wallet || userData?.public_address;
      let signerAddress = userData?.public_address;
      const userId = userData?.id;

      if (!userAddress || !signerAddress || !userId) {
        this.toastr.error('Wallet not connected');
        this.isProcessing = false;
        return;
      }

      // 1. Get Market Info (to get Token ID and verify IDs)
      const marketRes: any = await firstValueFrom(this.api.getMarketDetail(this.position.market_id));
      const marketData = marketRes.data;
      const outcome = marketData?.outcomes?.find((o: any) => o.id === this.position.outcome_id);

      if (!outcome || !outcome.token_id) throw new Error('Outcome token not found');

      const confirmedMarketId = marketData.id;
      const confirmedOutcomeId = outcome.id;
      const tokenId = outcome.token_id;

      // 2. Get Nonce
      const nonce = await this.web3Service.getUserNonce(userAddress);

      // 3. Prepare Order Data (Ensure price is in Dollars for backend)
      let priceUnit = this.latestStreamData.bid_price !== undefined ? this.latestStreamData.bid_price : 0;
      let priceInDollars = parseFloat(priceUnit);
      if (priceInDollars >= 1) {
        priceInDollars = priceInDollars / 100;
      }

      const shareCount = Number(this.latestStreamData.shares || 0);
      // const shareCount = 1;
      const orderData = {
        salt: Math.floor(Date.now() / 1000),
        maker: userAddress,
        signer: signerAddress,
        taker: "0x0000000000000000000000000000000000000000",
        tokenId: tokenId.toString(),
        makerAmount: parseUnits(shareCount.toString(), 6).toString(),
        takerAmount: parseUnits((priceInDollars * shareCount).toString(), 6).toString(),
        expiration: Math.floor(Date.now() / 1000) + 3600,
        nonce: Number(nonce),
        feeRateBps: 0,
        side: OrderSide.SELL,
        signatureType: 1
      };

      // 4. Sign Order
      const structured_data = {
        types: {
          EIP712Domain: [
            { name: "name", type: "string" },
            { name: "version", type: "string" },
            { name: "chainId", type: "uint256" },
            { name: "verifyingContract", type: "address" }
          ],
          Order: [
            { name: "salt", type: "uint256" },
            { name: "maker", type: "address" },
            { name: "signer", type: "address" },
            { name: "taker", type: "address" },
            { name: "tokenId", type: "uint256" },
            { name: "makerAmount", type: "uint256" },
            { name: "takerAmount", type: "uint256" },
            { name: "expiration", type: "uint256" },
            { name: "nonce", type: "uint256" },
            { name: "feeRateBps", type: "uint256" },
            { name: "side", type: "uint8" },
            { name: "signatureType", type: "uint8" }
          ]
        },
        primaryType: "Order",
        domain: {
          name: "Polymarket CTF Exchange",
          version: "1",
          chainId: 80002,
          verifyingContract: environment.CTF_EXCHANGE_ADDRESS
        },
        message: orderData
      };

      let signature;
      if (userData?.user_type?.toUpperCase() === 'WEB2') {
        try {
          const meta = await this.web2Service.getUserMetaData();
          const signingAddress = meta?.publicAddress || signerAddress;
          if (!signingAddress) throw new Error('No valid signing address for Web2 user');
          signature = await this.web2Service.signTypedData(signingAddress, structured_data);
        } catch (magicError: any) {
          console.error('Magic operation failed:', magicError);
          const isMagicLoggedIn = await this.web2Service.getAuthResult().catch(() => false);
          if (!isMagicLoggedIn) {
            throw new Error('Your Magic session has expired. Please log out and log in again.');
          }
          throw magicError;
        }
      } else {
        signature = await this.walletService.signTypedData(structured_data);
      }

      if (!signature) throw new Error('Failed to sign order');
      const hexSignature = signature.slice(2);

      // 5. Place Order
      const orderPayload = {
        outcome_id: confirmedOutcomeId,
        side: 'SELL',
        order_type: 'MARKET',
        shares: shareCount,
        price: priceInDollars,
        order_data: orderData,
        signature: hexSignature
      };

      await firstValueFrom(this.api.placeOrder(orderPayload, confirmedMarketId, userId));
      this.toastr.success('Cashout order placed');
      this.success.emit();

    } catch (error: any) {
      console.error('Cashout execution failed:', error);
      let errorMessage = error.message || 'Cashout failed';
      const lowerError = errorMessage.toLowerCase();

      if (lowerError.includes('user denied') || lowerError.includes('internal error') || lowerError.includes('unauthorized')) {
        errorMessage = 'Your session may have expired. Please try logging out and back in.';
      } else if (lowerError.includes('user rejected the request')) {
        errorMessage = 'User rejected the request';
      }

      this.toastr.error(errorMessage);
    } finally {
      this.isProcessing = false;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.cashoutStreamSubscription) this.cashoutStreamSubscription.unsubscribe();
  }
}
